import { fetchRaw, get } from "./client";

/**
 * Client for the awards spreadsheet import. A run is started by uploading the
 * workbook and then polled until it finishes: the import takes minutes, so
 * the start call only acknowledges the run and the result arrives on a later
 * read. Only one run may be in progress at a time — the start call answers
 * 409 when another one is, and the caller is expected to attach to that run
 * rather than retry.
 *
 * The endpoints live on awards-api and ride the staff-bff proxy.
 */

const BASE = "/v1/awards/import-runs";

export type ImportRunStatus = "running" | "succeeded" | "failed";

/**
 * Every count is optional: the summary is produced by the importer and the
 * page must keep rendering whatever subset an older or newer run recorded.
 */
export type ImportSummary = {
  imported?: Partial<{ inserted: number; updated: number; importable: number }>;
  incompleteByReason?: Record<string, number>;
  incompleteTotal?: number;
  skippedFirmLevel?: number;
  skippedCategoryOnly?: number;
  skippedUnknownAwardId?: number;
  duplicatesCollapsed?: number;
  parserRowErrors?: number;
  lawyerTiers?: Record<string, number>;
  jurisdictionBySource?: Record<string, number>;
  /** Importable rows per rule of the category cell resolver, keyed by rule. */
  categoryResolution?: Record<string, number>;
  /** Distinct categories among the importable rows. */
  distinctCategoriesCount?: number;
  /** Award types that are not on the reference list, by row count; the importer caps it at 50. */
  newAwardTypes?: Array<{ type: string; count: number }>;
  prunedAwardTypes?: number;
  prunedCategories?: number;
};

export type ImportRun = {
  id: string;
  status: ImportRunStatus;
  dryRun: boolean;
  clearExisting: boolean;
  fileName: string;
  startedBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  heartbeatAt: string | null;
  progress: { stage: "parsing" | "matching" | "importing"; done: number; total: number } | null;
  summary: ImportSummary | null;
  error: string | null;
};

export type StartImportOptions = {
  dryRun: boolean;
  clearExisting: boolean;
};

export type ImportRequestError = Error & { status?: number };

async function toError(response: Response, fallback: string): Promise<ImportRequestError> {
  let message = `${fallback}: HTTP ${response.status}`;
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === "string") message = body.error;
  } catch {
    // keep default
  }
  const error: ImportRequestError = new Error(message);
  error.status = response.status;
  return error;
}

export const awardsImportService = {
  /** Uploads the workbook and returns the acknowledged (running) run. */
  async start(file: File, options: StartImportOptions): Promise<ImportRun> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("dryRun", options.dryRun ? "true" : "false");
    formData.append("clearExisting", options.clearExisting ? "true" : "false");
    const response = await fetchRaw(BASE, { method: "POST", body: formData });
    if (!response.ok) {
      throw await toError(response, "Failed to start import");
    }
    const body = (await response.json()) as { run: ImportRun };
    return body.run;
  },

  /** Latest runs, newest first. */
  async list(): Promise<ImportRun[]> {
    const body = await get<{ runs: ImportRun[] }>(BASE);
    return body.runs;
  },

  async getRun(id: string): Promise<ImportRun> {
    const body = await get<{ run: ImportRun }>(`${BASE}/${encodeURIComponent(id)}`);
    return body.run;
  },

  /** The per-row CSV report for a finished run. */
  async downloadReport(id: string): Promise<Blob> {
    const response = await fetchRaw(`${BASE}/${encodeURIComponent(id)}/report`);
    if (!response.ok) {
      throw await toError(response, "Failed to download report");
    }
    return response.blob();
  },
};
